import type { DocNode } from '@atlaskit/adf-schema/doc';
import { scrubStr } from '@atlaskit/adf-utils/scrub';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { areNodesEqualIgnoreAttrs } from '@atlaskit/editor-common/utils/document';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { FireAnalyticEventCallback as ADFStreamerFireAnalyticEventCallback } from '@atlassian/ai-model-io/adf-streamer';
import { attributeError } from '@atlassian/editor-ai-common/analytics/attribute-error';
import type {
	Attribution,
	Confidence,
	DetectionLayer,
	OutcomeClass,
	SessionAbortReason,
} from '@atlassian/editor-ai-common/analytics/normalized-telemetry-event';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';
import type {
	EditorAIAnalyticEventPayload,
	ADFStreamingInfoAEP,
	ADFStreamingErrorAEP,
} from '@atlassian/editor-plugin-ai';

import { allStreamingCommands } from '../aiStreamingOrchestratorPluginType';
import type { API, StreamingCommand } from '../aiStreamingOrchestratorPluginType';
import type { QueuedStreamCommand } from '../types/StreamCommandQueue';
import type { ApplyErrorSymptom } from './utils/classify-apply-error';
import { getPmDoc } from './utils/get-pm-doc';
import { isAStreamableCommand } from './utils/is-a-streamable-command';
import { isAttributeOnlyChange } from './utils/is-attribute-only-change';

export function createFireADFStreamingAnalyticsEvent({
	api,
	isCreateAction,
	traceId,
	promptVersion,
}: {
	api: API;
	isCreateAction: boolean;
	promptVersion?: string;
	traceId: string | undefined;
}): ADFStreamerFireAnalyticEventCallback {
	return (event) => {
		const commonAttributes:
			| Omit<ADFStreamingErrorAEP['attributes'], 'errorMessage' | 'errorData'>
			| Omit<ADFStreamingInfoAEP['attributes'], 'message' | 'data'> = {
			traceId: traceId,
			...(promptVersion && fg('platform_editor_ai_promptversion_in_analytics')
				? { promptVersion }
				: {}),
			experienceName: null,
			selectionType: null,
			selectionCategory: null,
			streamId: null,
			source: { type: 'orchestrator', isCreateAction },
		};
		if (event.type === 'error') {
			api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
				action: 'error',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'adfStreaming',
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					...commonAttributes,
					errorMessage: event.message,
					errorData: event.data,
				},
			});
		} else {
			api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
				action: 'info',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'adfStreaming',
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					...commonAttributes,
					message: event.message,
					data: event.data,
				},
			});
		}
	};
}

type AttributeOnlyReplaceNodePayload = {
	action: 'replaceNodeAction';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'orchestratorReplaceNode';
	attributes: {
		attributeOnlyChange: boolean;
		nodeName: string | undefined;
	};
	eventType: EVENT_TYPE.TRACK;
};

export function fireAttributeOnlyReplaceNodeAnalytics({
	api,
	initialNode,
	lastDoc,
}: {
	api: API;
	initialNode: PMNode | undefined;
	lastDoc: DocNode | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<AttributeOnlyReplaceNodePayload, 'customEventType'>({
		action: 'replaceNodeAction',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'orchestratorReplaceNode',
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			attributeOnlyChange: isAttributeOnlyChange(api, initialNode, lastDoc),
			nodeName: initialNode?.type.name,
		},
	});
}

// Validates the command is valid ()
const validateCommandType = (cmd: StreamingCommand | undefined) =>
	cmd?.type && allStreamingCommands.includes(cmd.type) ? cmd.type : 'invalid';

export function checkAndFireNoDocumentChangeAnalytics({
	api,
	docBeforeStreaming,
	command,
	traceId,
}: {
	api: API;
	command: StreamingCommand | undefined;
	docBeforeStreaming: PMNode | undefined;
	traceId: string | undefined;
}): void {
	const docAfterStreaming = getPmDoc(api);
	if (docBeforeStreaming && docAfterStreaming) {
		const isSameDoc = docBeforeStreaming.eq(docAfterStreaming);
		const isSameDocIgnoreAttrs = areNodesEqualIgnoreAttrs(docBeforeStreaming, docAfterStreaming);
		if (isSameDoc || isSameDocIgnoreAttrs) {
			api.analytics?.actions.fireAnalyticsEvent({
				action: ACTION.NO_DOC_CHANGE_FOUND,
				actionSubject: ACTION_SUBJECT.AI_STREAMING,
				actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION,
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					command: validateCommandType(command),
					isSameDoc,
					isSameDocIgnoreAttrs,
					traceId: traceId,
				},
			});
		}
		// Attribution-first `toolCallAnomaly { symptom: 'no_document_change' }` for
		// streamable commands. The call site (streamCommandQueue) defers this call until
		// after handleFinalize's commit transaction lands (via an isStreaming subscription),
		// so by the time this runs, getPmDoc() reflects the committed doc.
		// isSameDoc is therefore accurate: true only when the stream genuinely produced
		// no structural change.
		//
		// The legacy NO_DOC_CHANGE_FOUND event above keeps the broader isSameDoc ||
		// isSameDocIgnoreAttrs check for dashboard continuity.
		if (
			command !== undefined &&
			isAStreamableCommand(command) &&
			expValEquals('platform_editor_ai_normalized_telemetry', 'isEnabled', true)
		) {
			// CCI-16980: For the toolCallAnomaly path we use a narrower comparator that
			// ignores only `localId` attributes. `replaceNode` always produces replacement
			// nodes with fresh localIds (CCI-16968 pattern), so `isSameDoc` alone would
			// miss genuine no-ops. However, the old `isSameDocIgnoreAttrs` was too broad
			// — it ignored ALL attributes including meaningful ones like heading `level`,
			// `panelType`, `background` etc., causing false positives when a replaceNode
			// intentionally changed only those attributes.
			//
			// `areNodesEqualIgnoreAttrs(…, ['localId'])` strips only localId before
			// comparing, so attribute-only changes to level/panelType/background etc. are
			// still detected as real edits and will NOT fire no_document_change.
			//
			// Short-circuit on isSameDoc: if docs are byte-for-byte identical the
			// localId-only traversal is redundant (isSameDoc ⊆ isSameDocIgnoreLocalIdsOnly).
			const isSameDocIgnoreLocalIdsOnly =
				isSameDoc || areNodesEqualIgnoreAttrs(docBeforeStreaming, docAfterStreaming, ['localId']);
			if (isSameDocIgnoreLocalIdsOnly) {
				fireToolCallResult({
					api,
					traceId,
					commandType: command.type,
					result: 'anomaly',
					symptom: 'no_document_change',
				});
			} else {
				fireToolCallResult({ api, traceId, commandType: command.type, result: 'success' });
			}
		}
	}
}

export function fireADFStreamingEndSessionErrorAnalytics({
	api,
	sessionStarted,
	endSessionCallCount,
	lastEndedSessionId,
	isDoubleCall,
}: {
	api: API;
	endSessionCallCount: number;
	isDoubleCall: boolean;
	lastEndedSessionId: string | null;
	sessionStarted: boolean;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'error',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreamingEndSession',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			sessionStarted,
			endSessionCallCount,
			lastEndedSessionId,
			isDoubleCall,
		},
	});
}

type PacerCompletionGapPayload = {
	action: 'completed';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'streamFlowPacerCompletion';
	attributes: {
		backendEndToPacerCompleteMs: number;
		backlogCharsAtBackendEnd: number;
		charsPerTickAtComplete: number;
		terminalType: string;
		traceId?: string;
	};
	eventType: EVENT_TYPE.OPERATIONAL;
};

/**
 * Fires a simple operational event capturing the gap between when the backend
 * finished streaming (terminal event pushed to the pacer) and when the pacer
 * finished draining it to the editor. Used to detect HTML+ streaming lag where
 * the editor keeps rendering after the backend has already completed.
 */
export function firePacerCompletionGapAnalytics({
	api,
	backendEndToPacerCompleteMs,
	backlogCharsAtBackendEnd,
	charsPerTickAtComplete,
	terminalType,
	traceId,
}: {
	api: API;
	backendEndToPacerCompleteMs: number;
	backlogCharsAtBackendEnd: number;
	charsPerTickAtComplete: number;
	terminalType: string;
	traceId?: string;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<PacerCompletionGapPayload, 'customEventType'>({
		action: 'completed',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'streamFlowPacerCompletion',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			backendEndToPacerCompleteMs,
			backlogCharsAtBackendEnd,
			charsPerTickAtComplete,
			terminalType,
			traceId,
		},
	});
}

export function fireSessionStartedAnalytics({
	api,
	sessionId,
	traceId,
	promptVersion,
}: {
	api: API;
	promptVersion?: string;
	sessionId: string;
	traceId: string | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'sessionStart',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'userFlow',
		eventType: EVENT_TYPE.UI,
		attributes: {
			aiGroupId: sessionId,
			actionTaken: undefined,
			editCount: 0,
			aiSessionId: '',
			traceIds: traceId ? [traceId] : undefined,
			...(promptVersion && fg('platform_editor_ai_promptversion_in_analytics')
				? { promptVersion }
				: {}),
		},
	});
}

export function fireToolCallCapturedAnalytics({
	api,
	command,
	actionId,
	traceId,
}: {
	actionId: string;
	api: API;
	command: StreamingCommand;
	traceId: string | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'captured',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'toolCall',
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			actionId,
			commandType: validateCommandType(command),
			contentType: 'contentType' in command ? command.contentType : undefined,
			traceId,
		},
	});
}

type SessionOutcomeAnalyticsPayload = {
	action: 'sessionOutcome';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'adfStreaming';
	attributes: {
		// Why the session was aborted. Only present when the session ended
		// via an abort and the reason classification is enabled; its presence
		// is what makes `outcomeClass: 'aborted'` possible.
		abortReason?: SessionAbortReason;
		attribution: Attribution;
		// Histogram of command types observed in the session, keyed by the
		// orchestrator's command-type enum. Bounded cardinality, no UGC.
		// Populated when `noMeaningfulEffect = true` and the session ran at
		// least one tool call; useful for debugging no-effect sessions.
		commandTypeCounts?: Record<string, number>;
		confidence: Confidence;
		detectionLayer: DetectionLayer;
		// Redacted error message for aborts forced by an error on the
		// streaming/apply path. Extracted via `ErrorUtils.extractErrorMessage`,
		// so emails and URLs are already scrubbed.
		errorMessage?: string;
		finalAdfInvalid: boolean;
		finalAdfRepaired: boolean;
		finalJsonInvalid: boolean;
		finalJsonRepaired: boolean;
		// True when any of the post-apply suspicious-signal flags
		// originated from a *heuristic* rather than a backend-supplied
		// signal (currently always true for `intentEffectMismatch` and
		// `unintendedRegionModified` until the backend supplies precise
		// `selectionLocalIds` / explicit intent labels). Lets dashboards
		// filter the noisier inferred signals out of headline KPIs.
		heuristicInferred?: boolean;
		// Post-apply analyzer flags. Each is `undefined` when the analyzer
		// did not run (e.g. session aborted before any apply). When the
		// analyzer ran, each is `true` / `false` based on the corresponding
		// structural-delta check.
		intentEffectMismatch?: boolean;
		noMeaningfulEffect?: boolean;
		outcomeClass: OutcomeClass;
		// `removedNodeCount` from the analyzer when
		// `unintendedRegionRemoved = true`. Lets dashboards distinguish
		// "single-node drift" from "session destroyed half the doc".
		removedNodeCount?: number;
		// True when `unintendedRegionRemoved` was derived from a
		// backend-supplied `selectionLocalIds` field (precise) rather than
		// the proxy fallback (any unintended region change). Always `false`
		// today; flips to `true` once CCI-15978 + the backend dependency
		// land. Lets dashboards segment precision over time.
		selectionLocalIdsAvailable?: boolean;
		symptom?: string;
		// Raw deltas from the post-apply analyzer when any suspicious
		// signal fired. Carried so dashboards can inspect magnitudes
		// without re-deriving them from the raw events.
		textCharacterDelta?: number;
		totalNodeDelta?: number;
		traceId: string | undefined;
		unintendedRegionModified?: boolean;
		unintendedRegionRemoved?: boolean;
	};
	eventType: EVENT_TYPE.OPERATIONAL;
};

/**
 * Fire the authoritative "one event per session" outcome signal called for in
 * the Phase 1 rollout plan of the Editor + Minion ADF streaming telemetry
 * design.
 *
 * Carries the four normalized contract fields (`detection_layer`,
 * `attribution`, `outcome_class`, `confidence`) plus the raw repair signals
 * derived from the ADF streamer so dashboards can drill into the underlying
 * cause without re-deriving them.
 *
 * Also carries the post-apply analyzer flags (`unintendedRegionRemoved`,
 * `unintendedRegionModified`, `noMeaningfulEffect`, `intentEffectMismatch`)
 * and their supporting metadata (`removedNodeCount`, `totalNodeDelta`,
 * `textCharacterDelta`, `commandTypeCounts`, `selectionLocalIdsAvailable`,
 * `heuristicInferred`) so a single event covers both "what happened" and "why it
 * was suspicious". Callers pass `undefined` for any flag/metadata that
 * doesn't apply (e.g. when the analyzer didn't run, or when the session was
 * not classified as suspicious).
 *
 * `attribution` and `confidence` are derived centrally via
 * {@link attributeError} from the supplied `(symptom, detectionLayer)` so
 * every event uses the same routing table. Callers therefore only need to
 * report a raw `symptom` string — they never hand-pick an attribution domain.
 */
export function fireSessionOutcomeAnalytics({
	api,
	traceId,
	outcomeClass,
	detectionLayer,
	symptom,
	finalJsonInvalid,
	finalJsonRepaired,
	finalAdfInvalid,
	finalAdfRepaired,
	unintendedRegionRemoved,
	unintendedRegionModified,
	noMeaningfulEffect,
	intentEffectMismatch,
	removedNodeCount,
	totalNodeDelta,
	textCharacterDelta,
	commandTypeCounts,
	selectionLocalIdsAvailable,
	heuristicInferred,
	abortReason,
	errorMessage,
}: {
	abortReason?: SessionAbortReason;
	api: API;
	commandTypeCounts?: Record<string, number>;
	detectionLayer: DetectionLayer;
	errorMessage?: string;
	finalAdfInvalid: boolean;
	finalAdfRepaired: boolean;
	finalJsonInvalid: boolean;
	finalJsonRepaired: boolean;
	heuristicInferred?: boolean;
	intentEffectMismatch?: boolean;
	noMeaningfulEffect?: boolean;
	outcomeClass: OutcomeClass;
	removedNodeCount?: number;
	selectionLocalIdsAvailable?: boolean;
	symptom?: string;
	textCharacterDelta?: number;
	totalNodeDelta?: number;
	traceId: string | undefined;
	unintendedRegionModified?: boolean;
	unintendedRegionRemoved?: boolean;
}): void {
	// When no symptom is reported (e.g. clean_success) the routing table
	// returns `unknown`/`low`, which is the correct contract value for "no
	// detectable failure to attribute".
	const { attribution, confidence } = attributeError(symptom ?? '', detectionLayer, abortReason);

	api?.analytics?.actions.fireAnalyticsEvent<SessionOutcomeAnalyticsPayload, 'customEventType'>({
		action: 'sessionOutcome',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			traceId,
			outcomeClass,
			attribution,
			confidence,
			detectionLayer,
			symptom,
			finalJsonInvalid,
			finalJsonRepaired,
			finalAdfInvalid,
			finalAdfRepaired,
			...(abortReason !== undefined ? { abortReason } : {}),
			...(errorMessage !== undefined ? { errorMessage } : {}),
			...(unintendedRegionRemoved !== undefined ? { unintendedRegionRemoved } : {}),
			...(unintendedRegionModified !== undefined ? { unintendedRegionModified } : {}),
			...(noMeaningfulEffect !== undefined ? { noMeaningfulEffect } : {}),
			...(intentEffectMismatch !== undefined ? { intentEffectMismatch } : {}),
			...(removedNodeCount !== undefined ? { removedNodeCount } : {}),
			...(totalNodeDelta !== undefined ? { totalNodeDelta } : {}),
			...(textCharacterDelta !== undefined ? { textCharacterDelta } : {}),
			...(commandTypeCounts !== undefined ? { commandTypeCounts } : {}),
			...(selectionLocalIdsAvailable !== undefined ? { selectionLocalIdsAvailable } : {}),
			...(heuristicInferred !== undefined ? { heuristicInferred } : {}),
		},
	});
}

export function fireCommandError({
	api,
	command,
	traceId,
	isCreateAction,
	error,
}: {
	api: API;
	command: QueuedStreamCommand | null;
	error: unknown;
	isCreateAction: boolean | undefined;
	traceId: string | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'error',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			errorMessage: 'toolCallError',
			errorData: {
				commandType: command?.type,
				commandReason: command?.type === 'endStream' ? command?.reason : undefined,
				errorMessage: ErrorUtils.extractErrorMessage(error),
			},
			traceId,
			experienceName: null,
			selectionType: null,
			selectionCategory: null,
			streamId: null,
			source: { type: 'orchestrator', isCreateAction: isCreateAction ?? false },
		},
	});
}

/**
 * Per-tool-call anomaly symptom union. Each value is a distinct apply-step
 * fault detected by the orchestrator:
 *
 * - `apply_failed_*` — an exception was thrown by the apply layer.
 * - `local_id_not_found` — command targeted a `localId` that does
 *   not exist in the document.
 * - `unexpected_node_removal` — a top-level node disappeared after a
 *   command that wasn't supposed to remove it.
 * - `no_document_change` — a mutating command produced no document change.
 *
 * All values are mutually exclusive within a single command's apply pass.
 */
export type ToolCallAnomalySymptom =
	| ApplyErrorSymptom
	| 'local_id_not_found'
	| 'unexpected_node_removal'
	| 'no_document_change';

/**
 * Payload type for the unified `toolCallResult` event — fired on every tool
 * call processed by the orchestrator's apply pipeline. The `result` field
 * discriminates between success and anomaly; anomaly-specific attributes are
 * present only when `result === 'anomaly'`.
 *
 * Exactly one event fires per tool call — either `result: 'success'` or
 * `result: 'anomaly'` — so anomaly rate is simply:
 *   COUNT(*) WHERE result = 'anomaly'
 *   ─────────────────────────────────
 *          COUNT(*) (all events)
 *
 * Exception: tool calls that throw a PM error fire only `result: 'anomaly'`
 * (from the `processQueue` catch block) with no corresponding success event.
 *
 * All `localId`-bearing fields are scrubbed via `scrubStr` before being
 * placed on the wire so any email/URL substrings the model may have echoed
 * into the IDs are redacted (the same precedent as `find-node-by-local-id`).
 *
 * `attribution` and `confidence` are derived via the shared `attributeError`
 * routing table for anomaly symptoms that have an entry; for
 * `no_document_change` they are omitted (the design doc says this signal
 * alone is insufficient to attribute fault).
 */
type ToolCallResultPayload = {
	action: 'toolCallResult';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'adfStreaming';
	attributes: {
		// Present only when result === 'anomaly':
		attribution?: Attribution;
		// Present on every toolCallResult event:
		commandType: string;
		confidence?: Confidence;
		detectionLayer: DetectionLayer;
		errorMessage?: string;
		result: 'success' | 'anomaly';
		scrubbedRemovedLocalIds?: string[];
		scrubbedTargetLocalId?: string;
		symptom?: ToolCallAnomalySymptom;
		traceId: string | undefined;
	};
	eventType: EVENT_TYPE.OPERATIONAL;
};

/**
 * Fires the unified `toolCallResult` event for every tool call processed by
 * the orchestrator's apply pipeline.
 *
 * Pass `result: 'success'` for tool calls that completed without a detected
 * anomaly. Pass `result: 'anomaly'` (the default) and supply `symptom` for
 * any per-tool-call fault. This single event on every tool call lets
 * dashboards compute anomaly rate as:
 *   COUNT(*) WHERE result = 'anomaly' / COUNT(*)
 *
 * The detection layer is always `'orchestrator'` — the orchestrator's apply
 * pipeline is what observes the result, regardless of whether the underlying
 * cause is upstream (model output) or local (apply-logic bug).
 *
 * Anomaly-only optional fields:
 * - `symptom`: discriminates the four mutually-exclusive anomaly classes.
 * - `errorMessage`: for `apply_failed_*` symptoms.
 * - `targetLocalId`: scrubbed via `scrubStr` before placement on the wire.
 * - `removedLocalIds`: scrubbed; for `unexpected_node_removal`.
 * - `attribution` / `confidence`: derived from the shared `attributeError`
 *   routing table; omitted for `no_document_change` (insufficient signal).
 */
/** Shared fields present on every `toolCallResult` event variant. */
type ToolCallResultBase = {
	api: API;
	commandType: string;
	traceId: string | undefined;
};

/** Discriminated union enforcing that anomaly events always carry a symptom. */
type ToolCallResultArgs =
	| (ToolCallResultBase & { result: 'success' })
	| (ToolCallResultBase & {
			errorMessage?: string;
			removedLocalIds?: string[];
			result: 'anomaly';
			/** Required for anomaly events — used to categorize in dashboards. */
			symptom: ToolCallAnomalySymptom;
			/**
			 * The model-supplied target `localId` (or `undefined` for commands
			 * without one). Scrubbed via `scrubStr` before placement on the wire.
			 */
			targetLocalId?: string;
	  });

export function fireToolCallResult(args: ToolCallResultArgs): void {
	const { api, traceId, commandType, result } = args;
	const symptom = args.result === 'anomaly' ? args.symptom : undefined;
	const errorMessage = args.result === 'anomaly' ? args.errorMessage : undefined;
	const targetLocalId = args.result === 'anomaly' ? args.targetLocalId : undefined;
	const removedLocalIds = args.result === 'anomaly' ? args.removedLocalIds : undefined;
	const routed =
		result === 'anomaly' && symptom !== undefined && symptom !== 'no_document_change'
			? attributeError(symptom, 'orchestrator')
			: undefined;

	api?.analytics?.actions.fireAnalyticsEvent<ToolCallResultPayload, 'customEventType'>({
		action: 'toolCallResult',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			result,
			commandType,
			detectionLayer: 'orchestrator',
			traceId,
			...(symptom !== undefined && { symptom }),
			...(routed !== undefined && {
				attribution: routed.attribution,
				confidence: routed.confidence,
			}),
			...(errorMessage !== undefined && { errorMessage }),
			...(targetLocalId !== undefined && { scrubbedTargetLocalId: scrubStr(targetLocalId) }),
			...(removedLocalIds !== undefined && {
				scrubbedRemovedLocalIds: removedLocalIds.map((id) => scrubStr(id)),
			}),
		},
	});
}

/**
 * Fires when {@link resumeAction} cannot find stored positions for an aborted
 * tool call and falls back to re-running the original command. The fallback
 * re-resolves append-style commands to the current end of document, so the
 * resumed content may land in the wrong place. This should not happen in
 * practice (content is always streamed before a timeout abort), so the event
 * surfaces the degradation for monitoring.
 */
type ResumeAbortedToolCallFallbackPayload = {
	action: 'anomaly';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'resumeAbortedToolCallFallback';
	attributes: {
		commandType: string;
	};
	eventType: EVENT_TYPE.OPERATIONAL;
};

// `commandType` is already a typed `command.type`, so no `validateCommandType` needed.
export function fireResumeAbortedToolCallFallbackAnalytics({
	api,
	commandType,
}: {
	api: API;
	commandType: string;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<
		ResumeAbortedToolCallFallbackPayload,
		'customEventType'
	>({
		action: 'anomaly',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'resumeAbortedToolCallFallback',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			commandType,
		},
	});
}
